import { Res } from '@cbo/shared-library';
import { DataGridPremiumProps, GridAggregationFunction, GridColTypeDef, GridSortModel } from '@mui/x-data-grid-premium';
import { isNaN, isNil } from 'lodash';
import { Theme } from '@mui/material/styles';
import { formatCurrency, getCurrencySymbol } from '../formatters';
import { BaseGridRow } from '../../components/DataGrid/DataGrid';
import SalesUtilities from '../../../sales/utilities';
import { dateComparator } from '../../components/DataGrid/helpers';
import CustomActiveCell from '../../components/DataGrid/CustomCells/CustomActiveCell';

// eslint-disable-next-line import/prefer-default-export
export const getTreeDataPath: DataGridPremiumProps['getTreeDataPath'] = (row) => row.hierarchy;

// `GridColTypeDef`s

// round to nearest integer
export const integerColDef = (showDashes?: boolean): GridColTypeDef => ({
  type: 'number',
  valueFormatter: (value: number) => {
    if (isNil(value)) {
      return '';
    }

    if (value === 0 && showDashes) {
      return '--';
    }

    return Math.round(Number(value));
  },
});

export const activeColDef = (fieldName: string): GridColTypeDef => ({
  renderCell: (params) => <CustomActiveCell isActive={params.row[fieldName]} />,
  headerAlign: 'center',
});

// round to 2 decimal places
export const hoursColDef: GridColTypeDef = {
  type: 'number',
  valueFormatter: (value: number) => {
    if (isNil(value)) {
      return '';
    }

    return Number(value).toFixed(2);
  },
};

// Display a column whose value is a number in seconds as hours,
// rounded to 2 decimal places.
//
// For example, 90 * 60 = 5400 (90 mins in seconds) -> "1.50".
// The valueGetter converts seconds to hours so filtering operates on hours.
export const humanReadableTimeDurationColDef: GridColTypeDef = {
  type: 'number',
  valueGetter: (value: number) => {
    if (isNil(value)) {
      return null;
    }
    return value / 3600;
  },
  valueFormatter: (value: number) => {
    if (isNil(value)) {
      return '';
    }
    return value.toFixed(2);
  },
};

// percentage
export const percentageColDef: GridColTypeDef = {
  type: 'number',
  valueFormatter: (value: number) => (isNaN(Number(value)) ? '' : `${(Number(value) * 100).toFixed(2)}%`),
};

// Date/Time
export const dateColDef: GridColTypeDef = {
  type: 'dateTime',
  valueGetter: (value: string) => value && new Date(value),
  valueFormatter: (value: string) => {
    if (!value) return '';
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  },
};

// Timezone-aware DateTime formatter
// Formats UTC datetime in the specified timezone from the row data, or local timezone as fallback
export const timezoneAwareDateColDef = (timezoneFieldName: string): GridColTypeDef => ({
  type: 'dateTime',
  valueGetter: (value: string) => value && new Date(value),
  valueFormatter: (value: string, row: Record<string, unknown>) => {
    if (!value) return '';

    const timezone = row?.[timezoneFieldName] as string | undefined;
    const date = new Date(value);

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    };

    // If timezone is provided, format in that timezone
    // Otherwise use local timezone
    if (timezone) {
      options.timeZone = timezone;
      return date.toLocaleString('en-US', options);
    }

    return date.toLocaleString(undefined, options);
  },
});

export const businessDateColDef = (preferences: Res.Admin.UserPreferences | null | undefined): GridColTypeDef => ({
  sortComparator: dateComparator,
  valueGetter: SalesUtilities.getDateOnly(preferences),
});

// currency
export const getCurrencyColDef = (
  preferences: Res.Admin.UserPreferences | null | undefined,
  displayDash = false
): GridColTypeDef => ({
  type: 'number',
  valueFormatter: (value: number) => {
    if (isNil(value)) {
      return displayDash ? '--' : '';
    }

    return formatCurrency(value, preferences);
  },
});

export const getCurrencySymbolColDef = (preferences: Res.Admin.UserPreferences | null | undefined): GridColTypeDef => ({
  type: 'number',
  valueFormatter: (value: number, row) => {
    if (isNil(value)) {
      return '';
    }

    const decimalPlaces = (value.toString().split('.')[1] || '').length;

    // If value has less than or equal to 2 decimals, we wouldn't want to display $0.1, or $0
    if (decimalPlaces <= 2) {
      return formatCurrency(value, preferences);
    }

    const currencySymbol = getCurrencySymbol(preferences);

    return `${currencySymbol}${value.toFixed(3)}`;
  },
});

export const tipPercentageAggregator = (
  tip: string,
  sale: string,
  label: string
): GridAggregationFunction<{ tips: number; sales: number }, number> => ({
  getCellValue: ({ row }) => ({
    tips: Number(row[tip]) || 0,
    sales: Number(row[sale]) || 0,
  }),
  apply: ({ values }) => {
    let totalTips = 0;
    let totalSales = 0;
    values.forEach((value) => {
      if (value) {
        totalTips += value.tips;
        totalSales += value.sales;
      }
    });
    return totalSales === 0 ? 0 : totalTips / totalSales;
  },
  columnTypes: ['number'],
  label,
});

// Misc. utility functions

export function getCurrencyColorString(value: number, theme: Theme): string | undefined {
  if (value > 0) return theme.palette.success.main;
  if (value < 0) return theme.palette.error.main;
  return undefined;
}

export function sortRows<T extends BaseGridRow>(arr: T[], sortModel: GridSortModel): T[] {
  return arr.sort((a, b) => {
    const { field, sort } = sortModel[0];
    const aValue = a[field] as string;
    const bValue = b[field] as string;
    if (sort === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });
}

export const ROW_GROUPING_ID = 'auto-generated-row-';
